import React from 'react';
import { ArrowRightIcon, MapPinIcon, PhoneIcon, Clock } from 'lucide-react';
import { DoorStrip, DOOR_PALETTE, WOOD_TEXTURE_CSS } from '../components/DoorStrip';
const LOGO = 'https://cdn.magicpatterns.com/uploads/ewfDCj5QKNDNeQRsTcwjBG/logo_de_Junquito_Gas_(4).ai_(4).png';
export function HomePage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="w-full bg-white" data-id="element-280">
      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-id="element-281">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=80)'
      }} data-id="element-282" />
        <div className="absolute inset-0 bg-black/60" data-id="element-283" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 w-full" data-id="element-284">
          <div className="max-w-xl" data-id="element-285">
            {/* Door palette dots */}
            <div className="flex gap-1.5 mb-8" data-id="element-286">
              {DOOR_PALETTE.map((c, i) => <div key={i} className="w-6 h-1 rounded-full" style={{
              backgroundColor: c
            }} data-id="element-287" />)}
            </div>

            <h1 className="font-display text-6xl md:text-8xl text-white mb-6 leading-[0.95] font-extrabold tracking-tight" data-id="element-288">
              Donde el sabor es arte.
            </h1>

            <p className="text-lg text-white/80 mb-10 max-w-sm leading-relaxed font-medium" data-id="element-289">
              Cocina honesta con ingredientes locales. Una experiencia que
              despierta todos los sentidos.
            </p>

            <div className="flex flex-wrap gap-3" data-id="element-290">
              <button onClick={() => onNavigate('order')} className="px-8 py-4 bg-white text-black rounded-xl font-bold text-sm hover:bg-stone-100 transition-colors shadow-lg" data-id="element-291">
                Pedir Ahora
              </button>
              <button onClick={() => onNavigate('menu')} className="px-8 py-4 text-white border border-white/30 rounded-xl font-bold text-sm hover:bg-white/10 transition-colors backdrop-blur-sm" data-id="element-292">
                Ver Menú
              </button>
              <button onClick={() => onNavigate('reservas')} className="px-8 py-4 text-white/90 font-bold text-sm hover:text-white transition-colors flex items-center gap-2" data-id="element-293">
                Reservar Mesa <ArrowRightIcon className="w-4 h-4" data-id="element-294" />
              </button>
            </div>
          </div>
        </div>

        {/* Door strip at bottom of hero */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-295">
          <DoorStrip height="h-3" data-id="element-296" />
        </div>
      </section>

      {/* ── INFO BAR ── */}
      <section className="bg-black py-6 px-4 border-b border-stone-900" data-id="element-297">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-x-12 gap-y-4 text-sm font-medium text-stone-300" data-id="element-298">
          <div className="flex items-center gap-3" data-id="element-299">
            <MapPinIcon className="w-4 h-4" style={{
            color: DOOR_PALETTE[3]
          }} data-id="element-300" />
            Plaza Jardines, Santiago
          </div>
          <div className="flex items-center gap-3" data-id="element-301">
            <PhoneIcon className="w-4 h-4" style={{
            color: DOOR_PALETTE[1]
          }} data-id="element-302" />
            +1 (809) 247-7373
          </div>
          <div className="flex items-center gap-3" data-id="element-303">
            <Clock className="w-4 h-4" style={{
            color: DOOR_PALETTE[5]
          }} data-id="element-304" />
            Lun-Dom: 10AM – 11PM
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="bg-white pb-20" data-id="element-305">
        {/* Section banner */}
        <div className="relative overflow-hidden mb-16" style={{
        backgroundColor: DOOR_PALETTE[1],
        backgroundImage: WOOD_TEXTURE_CSS
      }} data-id="element-306">
          <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between" data-id="element-307">
            <div data-id="element-308">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-1" data-id="element-309">
                Nuestra Esencia
              </h2>
              <p className="text-white/70 text-xs font-bold uppercase tracking-widest pl-1" data-id="element-310">
                ¿Por qué La Cochera?
              </p>
            </div>
            <span className="font-display text-8xl font-extrabold text-white/10 leading-none select-none hidden md:block" data-id="element-311">
              03
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-312">
          {[{
          img: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&q=80',
          title: 'Ingredientes Frescos',
          desc: 'Seleccionamos lo mejor del mercado local cada mañana. Sin congelados, sin atajos.',
          color: DOOR_PALETTE[0]
        }, {
          img: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80',
          title: 'Cocina de Autor',
          desc: 'Recetas tradicionales perfeccionadas con técnicas modernas y nuestro toque especial.',
          color: DOOR_PALETTE[1]
        }, {
          img: 'https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?w=600&q=80',
          title: 'Ambiente Único',
          desc: 'Un espacio diseñado para disfrutar. Música, luz y confort en cada detalle.',
          color: DOOR_PALETTE[3]
        }].map((f, i) => <div key={i} className="group relative bg-white rounded-xl overflow-hidden border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-id="element-313">
              {/* Left accent bar */}
              <div className="absolute left-0 top-0 bottom-0 w-1.5 z-10" style={{
            backgroundColor: f.color
          }} data-id="element-314" />
              <div className="h-64 overflow-hidden relative" data-id="element-315">
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors z-10" data-id="element-316" />
                <img src={f.img} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-317" />
              </div>
              <div className="p-8 pl-10" data-id="element-318">
                <h3 className="font-display font-bold text-stone-900 text-xl mb-3" data-id="element-319">
                  {f.title}
                </h3>
                <p className="text-stone-500 text-sm leading-relaxed font-medium" data-id="element-320">
                  {f.desc}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      <DoorStrip height="h-3" data-id="element-321" />

      {/* ── HIGHLIGHT DISH ── */}
      <section className="relative py-32 px-4 overflow-hidden" data-id="element-322">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1544025162-d76694265947?w=1600&q=80)'
      }} data-id="element-323" />
        <div className="absolute inset-0 bg-black/75" data-id="element-324" />

        <div className="relative z-10 max-w-4xl mx-auto text-center text-white" data-id="element-325">
          <div className="inline-block px-5 py-2 rounded-full mb-8 text-white text-xs font-bold uppercase tracking-widest shadow-lg" style={{
          backgroundColor: DOOR_PALETTE[2],
          backgroundImage: WOOD_TEXTURE_CSS
        }} data-id="element-326">
            Especial de la Casa
          </div>
          <h2 className="font-display text-6xl md:text-8xl font-extrabold tracking-tight mb-6 leading-none" data-id="element-327">
            Costillas BBQ
          </h2>
          <p className="text-white/70 text-lg mb-12 max-w-xl mx-auto leading-relaxed font-medium" data-id="element-328">
            Marinadas 24 horas, bañadas en salsa BBQ casera y acompañadas de
            papas rústicas.
          </p>
          <button onClick={() => onNavigate('order')} className="px-10 py-4 bg-white text-black rounded-xl font-bold text-sm hover:bg-stone-100 transition-colors shadow-xl hover:scale-105 transform duration-200" data-id="element-329">
            Pedir • RD$ 22.00
          </button>
        </div>
      </section>

      <DoorStrip inverted height="h-3" data-id="element-330" />

      {/* ── CTA ── */}
      <section className="bg-black py-24 px-4" data-id="element-331">
        <div className="max-w-3xl mx-auto text-center" data-id="element-332">
          <img src={LOGO} alt="La Cochera" className="h-16 mx-auto mb-10 opacity-90" data-id="element-333" />
          <h2 className="font-display text-4xl md:text-6xl text-white mb-6 font-extrabold tracking-tight" data-id="element-334">
            ¿Listo para disfrutar?
          </h2>
          <p className="text-stone-400 mb-12 text-lg font-medium" data-id="element-335">
            Reserva tu mesa o pide para llevar.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-336">
            <button onClick={() => onNavigate('reservas')} className="px-8 py-4 bg-white text-black rounded-xl font-bold text-sm transition-colors hover:bg-stone-100" data-id="element-337">
              Hacer Reserva
            </button>
            <button onClick={() => onNavigate('order')} className="px-8 py-4 border border-stone-800 text-stone-300 rounded-xl font-bold text-sm transition-colors hover:border-white hover:text-white" data-id="element-338">
              Ver Menú y Pedir
            </button>
          </div>
        </div>
      </section>
    </div>;
}